import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useIsDesktop } from '../../../hooks/useMediaQuery';
import { TaskyIconButton } from '../button/TaskyIconButton';
import { TaskyHeaderActions } from './TaskyHeaderActions';
import { useIsEmbeddedShell } from './EmbeddedShellContext';

export interface ResponsiveAppShellProps {
  sidebar: React.ReactNode;
  mobileSidebar?: React.ReactNode;
  topbarTitle?: React.ReactNode;
  topbarActions?: React.ReactNode;
  /**
   * 既定 true。モバイルのトップバー右端にヘルプ・クレジット残高のピルを出す。
   * ピルを自前で描くページ（チャットv2 など）だけ false にする。
   */
  topbarHeaderActions?: boolean;
  /**
   * sidebar がアプリ全体のメニューではなく、このページ専用の欄（チャットv2の履歴など）のとき true。
   * 別の枠（新UI /test-ui）の中でも、サイドバーとスマホのトップバーを残す。
   */
  ownSidebar?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const ResponsiveAppShell: React.FC<ResponsiveAppShellProps> = ({
  sidebar,
  mobileSidebar,
  topbarTitle,
  topbarActions,
  topbarHeaderActions = true,
  ownSidebar = false,
  className,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // トップバーは lg:hidden。PC では描かない（隠れたクレジット残高が取得を二重に走らせないように）
  const isDesktop = useIsDesktop();
  const embedded = useIsEmbeddedShell();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (embedded && !ownSidebar) {
    return (
      <div className={cn('min-h-screen bg-warm-bg font-sans', className)}>
        <main className="min-w-0 overflow-x-hidden">{children}</main>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen bg-warm-bg font-sans lg:flex', className)}>
      <div className="hidden h-screen min-h-0 shrink-0 lg:block">
        {sidebar}
      </div>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-2 border-b border-warm-border bg-white px-3 lg:hidden">
          <TaskyIconButton
            aria-label="メニューを開く"
            type="button"
            variant="ghost"
            onClick={() => setOpen(true)}
          >
            <Menu />
          </TaskyIconButton>
          <div className="min-w-0 flex-1 truncate text-center text-sm font-bold text-dark">
            {topbarTitle}
          </div>
          <div className="flex min-w-10 shrink-0 items-center justify-end gap-2">
            {topbarActions}
            {/* sticky のトップバーに入れれば、PrivateRoute の固定ピルのように本文へ重ならない */}
            {topbarHeaderActions && !isDesktop && <TaskyHeaderActions compact />}
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden">
          {children}
        </main>
      </div>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              aria-label="メニューを閉じる"
              className="absolute inset-0 bg-black/40"
              onClick={() => setOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 flex w-[min(86vw,320px)] max-w-full flex-col overflow-hidden bg-warm-card shadow-card">
              <div className="flex shrink-0 justify-end border-b border-warm-border bg-white p-2">
                <TaskyIconButton
                  aria-label="メニューを閉じる"
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                >
                  <X />
                </TaskyIconButton>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {mobileSidebar ?? sidebar}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
